import React, { useState, useEffect, useContext } from "react";
import styled from "@emotion/styled";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Card from "@mui/material/Card";
import { Link, useParams } from "react-router-dom";
import CreditCardIcon from "@mui/icons-material/CreditCard";

import { getProfile, orderSuccess, postCheckout } from "../utils/api";
import { MainContext } from "./Main";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import { calculatePrice } from "../utils/helpers";
import Tooltip from "@mui/material/Tooltip";
import { IProfile } from "../interfaces";

// Product IDs
const MONTHLY_STRIPE_SUB_ID =
  process.env.NODE_ENV === "development"
    ? "price_1QgYPtIwJheCLZQ8M02iVbym"
    : "price_1QgYH4IwJheCLZQ8BGCTXimG";

type productDisplayProps = {
  setSessionId: React.Dispatch<React.SetStateAction<string>>;
  setSuccess: React.Dispatch<React.SetStateAction<boolean>>;
  setMessage: React.Dispatch<React.SetStateAction<string>>;
  profile: IProfile | null;
};
const ProductDisplay = ({ setMessage, profile }: productDisplayProps) => {
  const context = useContext(MainContext);

  const checkoutClick = (event: React.MouseEvent<HTMLElement>) => {
    onCheckout("subscribe");
  };

  const onCheckout = async (checkOutType: string) => {
    // Stripe checkout flow
    let res = null;
    // The below price_ids come from the Stripe dashboard
    if (checkOutType === "subscribe") {
      res = await postCheckout(
        MONTHLY_STRIPE_SUB_ID,
        "ASANA",
        0,
        profile?.id as string,
      );
    }

    if (res?.ok) {
      const resData = await res.json();
      if (resData === "User already a subscriber") {
        setMessage(
          "You are already a subscriber. Please refresh your browser or send us a message for help.",
        );
      } else if (resData?.url) {
        window.location.replace(resData.url);
      }
    } else {
      setMessage(
        "Something went wrong. Please try again or send us a message for help.",
      );
    }
  };

  const UserInfoBox = () => {
    // If user is logged in, or they have visited this url with a valid profile id in the url
    let freeTrialDaysRemaining = 0;
    let intercomAdminSeats = 0;
    let asanaMembershipActive = false;

    if (context?.user) {
      freeTrialDaysRemaining = context.user?.asanaFreeTrialDaysRemaining;
      intercomAdminSeats = context.user?.intercomAdminSeats;
      asanaMembershipActive = context.user?.asanaMembershipActive;
    } else if (profile?.id) {
      freeTrialDaysRemaining = profile?.asanaFreeTrialDaysRemaining || 0;
      intercomAdminSeats = profile?.intercomAdminSeats;
      asanaMembershipActive = profile?.asanaMembershipActive;
    }

    if (context?.user?.isAsanaUser || profile?.id) {
      return (
        <Card className="pricing-card">
          <Typography variant="body1" gutterBottom className="top-gap">
            Days until your free trial expires:{" "}
            <span className="emphasize-text">
              {freeTrialDaysRemaining > 0
                ? freeTrialDaysRemaining
                : "Free trial expired"}
            </span>
          </Typography>
          <Typography variant="body1" gutterBottom>
            <div style={{ display: "flex", alignItems: "center" }}>
              Your Intercom Inbox seats:
              <span
                className="emphasize-text"
                style={{ marginLeft: "0.25rem" }}
              >
                {intercomAdminSeats}
              </span>
              <Tooltip title="The number of Inbox seats you have active. If this looks incorrect, please contact us">
                <HelpOutlineIcon className="help-icon" fontSize="small" />
              </Tooltip>
            </div>
          </Typography>
          <Typography variant="body1" gutterBottom>
            Your price per month:{" "}
            <span className="emphasize-text">
              ${calculatePrice(intercomAdminSeats)}
            </span>{" "}
            {intercomAdminSeats > 0 && (
              <span>
                ({"$"}
                {(
                  calculatePrice(intercomAdminSeats) / intercomAdminSeats
                ).toFixed(2)}{" "}
                per seat)
              </span>
            )}
          </Typography>

          {asanaMembershipActive ? (
            <Button className="login-button" variant="text">
              Subscribed!
            </Button>
          ) : (
            calculatePrice(intercomAdminSeats) > 0 && (
              <>
                <Button
                  className="login-button"
                  id="subscribe"
                  variant="contained"
                  onClick={checkoutClick}
                >
                  <CreditCardIcon sx={{ marginRight: "8px" }} />
                  Subscribe
                </Button>
              </>
            )
          )}
        </Card>
      );
    } else {
      return null;
    }
  };

  return (
    <Styled>
      <div className="product">
        <div className="description">
          <Typography variant="h1" gutterBottom>
            Asana in the Inbox Pricing
          </Typography>
        </div>

        <div className="pricing-cards-container">
          <UserInfoBox />

          <Card className="pricing-card">
            <Typography variant="body1" gutterBottom>
              Our free plan includes:
            </Typography>
            <ul>
              <Typography variant="body1" gutterBottom>
                <li>Create unlimited Asana tasks from Intercom</li>
                <li>Assign task to a user and set a due date from Intercom</li>
                <li>Standard attributes included in task description</li>
                <li>Unlink tasks</li>
              </Typography>
            </ul>
            <Typography variant="body1" gutterBottom>
              Our paid plans ALSO include:
            </Typography>
            <ul>
              <Typography variant="body1" gutterBottom>
                <li>Receive updates on tasks in Intercom</li>
                <li>Link existing tasks to tickets/conversations</li>
                <li>Comment on tasks from Intercom</li>
                <li>Select Intercom attributes to inlcude in tasks</li>
                <li>Populate Asana custom fields from Intercom</li>
                <li>Responsive support</li>
                <li>Cancel anytime</li>
              </Typography>
            </ul>
            <Typography variant="body1" gutterBottom>
              Our paid plans include enough features to keep your agents almost
              entirely out of Asana and focused on the customer!
            </Typography>
            <Typography variant="body1" gutterBottom>
              Pricing is based on the number of Intercom Inbox seats you have.
              The price per user decreases, the more seats you have.
            </Typography>

            <TableContainer component={Paper} className="top-gap">
              <Table className="table-container" aria-label="simple table">
                <TableHead>
                  <TableRow>
                    <TableCell>Intercom Inbox seats</TableCell>
                    <TableCell align="right">Price / month</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow>
                    <TableCell>1-5</TableCell>
                    <TableCell align="right">
                      $10 per month (flat fee)
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>6-10</TableCell>
                    <TableCell align="right">$5 per user</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>11-20</TableCell>
                    <TableCell align="right">$4 per user</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>21-50</TableCell>
                    <TableCell align="right">$3 per user</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>51+</TableCell>
                    <TableCell align="right">$2 per user</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </Card>
        </div>
      </div>
    </Styled>
  );
};

const SuccessDisplay = () => {
  return (
    <Styled>
      <div className="product Box-root">
        <div className="description Box-root">
          <h3>Subscription successful!</h3>
          <Typography variant="body1">
            Thank you! Your subscription was successful and we have sent a
            confirmation email to you.
          </Typography>
          <Typography variant="body1">
            You can manage your subscription any time by logging in and clicking
            the user avatar in the top right.
          </Typography>
          <div className="success-continue-btn">
            <Link to={"/"}>
              <Button variant="contained">Continue</Button>
            </Link>
          </div>
        </div>
      </div>
    </Styled>
  );
};

type messagePropsType = {
  message: string;
};
const Message = ({ message }: messagePropsType) => (
  <section>
    <Typography variant="body1">{message}</Typography>
  </section>
);

export default function PricingAsana() {
  const [message, setMessage] = useState("");
  const [success, setSuccess] = useState(false);
  const [sessionId, setSessionId] = useState("");
  const [profileId, setProfileId] = useState<string | null>(
    useParams().profileId || null,
  );
  const [profile, setProfile] = useState<IProfile | null>(null);
  const context = useContext(MainContext);

  useEffect(() => {
    const getProfileAsync = async (profileId: string) => {
      const profileResponse = await getProfile(profileId);
      setProfile(profileResponse.profile);
    };

    if (profileId === null) {
      if (context.user) {
        setProfileId(context.user.profileId);
      }
    } else {
      getProfileAsync(profileId);
    }
  }, [profileId, context.user]);

  useEffect(() => {
    const onSuccess = async (newSessionId: string) => {
      // We don't need to provide a referral code for Stripe here because we provide it in the checkout session
      await orderSuccess(newSessionId, profileId);
      await context.refreshUser();
    };

    // Check to see if this is a redirect back from Checkout
    const query = new URLSearchParams(window.location.search);

    if (query.get("success")) {
      const newSessionId = query.get("session_id") || "";
      if (!success) {
        onSuccess(newSessionId);
        setSuccess(true);
      }
      setSessionId(newSessionId);
    }

    if (query.get("canceled")) {
      setSuccess(false);
      setMessage("Order canceled.");
    }
  }, [sessionId, success, context, profileId]);

  if (!success && message === "") {
    return (
      <ProductDisplay
        setSuccess={setSuccess}
        setSessionId={setSessionId}
        setMessage={setMessage}
        profile={profile}
      />
    );
  } else if (success && sessionId !== "") {
    return <SuccessDisplay />;
  } else {
    return (
      <>
        <Message message={message} />
        <ProductDisplay
          setSuccess={setSuccess}
          setSessionId={setSessionId}
          setMessage={setMessage}
          profile={profile}
        />
      </>
    );
  }
}

const Styled = styled.div`
  display: flex;
  flex-direction: column;

  .bold {
    font-weight: bold;
  }

  .success-continue-btn {
    margin-top: 20px;
  }

  .pricing-cards-container {
    display: flex;
    flex-direction: column;
    flex-wrap: wrap;
    justify-content: flex-start;
    margin: 2rem 0 1rem 0;
    gap: 2rem;

    .pricing-card {
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      justify-content: flex-start;
      width: 100%;
      max-width: 700px;
      min-height: 200px;
      padding: 1rem;

      .login-button {
        margin: 2rem 0 0.5rem 0;
        width: 220px;
      }
    }
  }

  .questions-btn {
    margin-top: 2rem;
  }

  .top-gap {
    margin-top: 3rem;
  }

  .table-container {
    max-width: 400px;
  }

  .emphasize-text {
    font-weight: bold;
  }

  .help-icon {
    margin-left: 0.3rem;
  }
`;
